/**
 * Command Palette Actions
 *
 * Defines all available actions in the command palette
 */

export interface CommandAction {
  id: string;
  label: string;
  category: 'Navigation' | 'Actions' | 'Settings';
  icon: string;
  keywords: string[]; // For fuzzy search
  action: () => void;
}

export function createCommandActions(navigate: (path: string) => void): CommandAction[] {
  return [
    // Navigation
    {
      id: 'nav-dashboard',
      label: 'Go to Dashboard',
      category: 'Navigation',
      icon: '📊',
      keywords: ['dashboard', 'home', 'overview'],
      action: () => navigate('/dashboard'),
    },
    {
      id: 'nav-conversations',
      label: 'Go to Conversations',
      category: 'Navigation',
      icon: '💬',
      keywords: ['conversations', 'messages', 'chat'],
      action: () => navigate('/conversations'),
    },
    {
      id: 'nav-agents',
      label: 'Go to Agents',
      category: 'Navigation',
      icon: '🤖',
      keywords: ['agents', 'bots', 'ai'],
      action: () => navigate('/agents'),
    },
    {
      id: 'nav-activity',
      label: 'Go to Activity Logs',
      category: 'Navigation',
      icon: '📜',
      keywords: ['activity', 'logs', 'history', 'audit'],
      action: () => navigate('/activity'),
    },
    {
      id: 'nav-mcp-servers',
      label: 'Go to MCP Servers',
      category: 'Navigation',
      icon: '🔌',
      keywords: ['mcp', 'servers', 'integrations', 'plugins'],
      action: () => navigate('/mcp-servers'),
    },

    // Quick Actions
    {
      id: 'action-new-conversation',
      label: 'Create New Conversation',
      category: 'Actions',
      icon: '➕',
      keywords: ['new', 'create', 'conversation', 'chat'],
      action: () => navigate('/conversations'), // Will open with new conversation UI
    },
    {
      id: 'action-new-agent',
      label: 'Create New Agent',
      category: 'Actions',
      icon: '🆕',
      keywords: ['new', 'create', 'agent', 'bot'],
      action: () => navigate('/agents'), // Will open with new agent UI
    },
    {
      id: 'action-new-mcp-server',
      label: 'Add MCP Server',
      category: 'Actions',
      icon: '🔧',
      keywords: ['new', 'add', 'mcp', 'server', 'integration'],
      action: () => navigate('/mcp-servers'), // Will open with add server UI
    },
  ];
}

/**
 * Fuzzy search implementation for command palette
 * Matches query against action labels and keywords
 */
export function fuzzySearchActions(actions: CommandAction[], query: string): CommandAction[] {
  if (!query.trim()) {
    return actions;
  }

  const lowerQuery = query.toLowerCase();

  return actions
    .map((action) => {
      const labelMatch = action.label.toLowerCase().includes(lowerQuery);
      const keywordMatch = action.keywords.some((kw) => kw.toLowerCase().includes(lowerQuery));

      // Score: label match gets higher priority
      const score = labelMatch ? 10 : keywordMatch ? 5 : 0;

      return { action, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ action }) => action);
}
